import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IdentityService } from '../../../../services/identity/identity.service';
import { ConfirmRecoverRequest } from '../../../../models/auth/confirm-recover-request';

@Component({
  selector: 'app-reset-password-form',
  imports: [FormsModule],
  templateUrl: './reset-password-form.html',
  styleUrl: './reset-password-form.scss',
})
export class ResetPasswordForm {
  private identityService = inject(IdentityService);
  recoveryUsername = input.required<string>();
  resetToken = input.required<string>();

  newPassword = signal('');
  confirmNewPassword = signal('');

  newPasswordStrength = computed(() => this.calculatePasswordStrength(this.newPassword()));
  passwordsMatch = computed(() => this.newPassword() && this.newPassword() === this.confirmNewPassword());

  changeView = output<'login'>();
  passwordReset = output<void>();

  resetPassword() {
    if (!this.passwordsMatch() || this.newPassword().length < 8) {
      console.error('Passwords do not match or are too short.');
      return;
    }

    let request = new ConfirmRecoverRequest();
    request.userName = this.recoveryUsername();
    request.password = this.newPassword();
    request.confirmPassword = this.confirmNewPassword();
    request.resetToken = this.resetToken();
    request.resetMethod = 1;

    this.identityService.resetPassword(request).subscribe({
      next: (response) => {

        // Switch back to login after pw reset.
        this.changeView.emit('login');

        // Reset state and emit event
        this.newPassword.set('');
        this.confirmNewPassword.set('');
        this.passwordReset.emit();

        console.log(`Password has been reset for ${this.recoveryUsername()}`);
      },
      error: (error) => {
        console.error(error);

        // Reset state and emit event
        this.newPassword.set('');
        this.confirmNewPassword.set('');
        this.passwordReset.emit();
      }
    });
  }

  // --- Password Strength Logic ---
  private calculatePasswordStrength(p: string) {
    let level = 0;
    let text = '';

    if (!p) {
      return { level, text };
    }

    const hasUppercase = /[A-Z]/.test(p);
    const hasLowercase = /[a-z]/.test(p);
    const hasNumbers = /[0-9]/.test(p);
    const hasSymbols = /[^A-Za-z0-9]/.test(p);
    const isLongEnough = p.length >= 8;

    let strengthPoints = 0;
    if (hasUppercase) strengthPoints++;
    if (hasLowercase) strengthPoints++;
    if (hasNumbers) strengthPoints++;
    if (hasSymbols) strengthPoints++;

    if (isLongEnough) {
      if (strengthPoints >= 4) {
        level = 4; text = 'Very Strong';
      } else if (strengthPoints >= 3) {
        level = 3; text = 'Strong';
      } else if (strengthPoints >= 2) {
        level = 2; text = 'Medium';
      } else {
        level = 1; text = 'Weak';
      }
    } else {
      level = 1; text = 'Weak';
    }

    return { level, text };
  }

  // --- UI Helpers ---
  getStrengthClass(level: number): string {
    switch (level) {
      case 1: return 'text-danger';
      case 2: return 'text-warning';
      case 3: return 'text-info';
      case 4: return 'text-success';
      default: return 'text-secondary';
    }
  }

  getStrengthBarClass(barNumber: number, strengthLevel: number): string {
    if (barNumber > strengthLevel) {
      return '';
    }
    switch (strengthLevel) {
      case 1: return 'bg-danger';
      case 2: return 'bg-warning';
      case 3: return 'bg-info';
      case 4: return 'bg-success';
      default: return '';
    }
  }
}
